/**
 * ChartMenu — the ⋯ button in the chart page header and its small popover (Edit / Delete).
 * Closes on an outside click, Escape and after an item is chosen; focus returns to the button.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChartPage.module.css';

export interface ChartMenuProps {
  onEdit: () => void;
  /** Absent → no «Удалить» item (the own chart cannot be deleted). */
  onDelete?: () => void;
}

export function ChartMenu({ onEdit, onDelete }: ChartMenuProps): JSX.Element {
  const { t } = useTranslation('me');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = (): void => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e: Event): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const choose = (action: () => void) => (): void => {
    setOpen(false);
    // Focus goes back to ⋯ first; a modal opened by the action then takes it on purpose.
    buttonRef.current?.focus();
    action();
  };

  return (
    <div
      className={styles.menuRoot}
      ref={rootRef}
      onKeyDown={onKeyDown}
      onBlur={(e) => {
        // Tabbing out of the open menu closes it.
        if (open && !rootRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        ref={buttonRef}
        className={styles.menuButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('charts.page.menu')}
        onClick={() => setOpen((v) => !v)}
        data-testid="chart-menu"
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div className={styles.menu} role="menu" ref={menuRef}>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={choose(onEdit)}
            data-testid="chart-menu-edit"
          >
            {t('charts.page.edit')}
          </button>
          {onDelete && (
            <button
              type="button"
              role="menuitem"
              className={`${styles.menuItem} ${styles.menuItemDanger}`}
              onClick={choose(onDelete)}
              data-testid="chart-menu-delete"
            >
              {t('charts.page.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
